/**
 * liPai 类型策略：立牌（外容器矩形 + 内插口矩形）。
 *
 * 固定 bottom 贴底边（spec §3.2）：
 *   - 外容器（移动范围）仅在 addComponent / 无缓存 placement 时 initContainer 一次，之后冻结；
 *   - 内插口仅在外容器内左右拖；刀线刷新不重算外容器。
 *
 * 刀线刷新由 orchestrator 统一接管（add / touchend slot 变化 / remove）。
 *
 * 导出（spec §7）：
 *   socketInfoList:        [{ point, width, height, slotRect, componentId }]
 *   originSocketInfoList:  [{ point, width, height, slotRect, containerPoint,
 *                              containerWidth, containerHeight, baseCanvasWidth, ... }]
 */

import { DRAW_TYPE, DEFAULT_CONFIG, lipaiColor } from '../constants.js';
import { registerComponentType } from '../typeRegistry.js';
import {
  convertCanvasToBackend,
  getRestoreBusinessScale,
  rectCanvasToApiSlotRect,
  rectCanvasToSlotRect
} from '../core.js';

const LIPAI_SLOT_RECT_OFFSET_OUT_OF_RANGE = 'slotRectOffsetOutOfRange';
const LIPAI_SLOT_RECT_OUT_OF_RANGE_TOLERANCE_RATIO = 0.01;

/**
 * 校验立牌 geometryConfig。
 * @param {Object} g - geometryConfig
 * @returns {string|null} 错误文案；null 表示通过
 */
function validateGeometryConfig(g) {
  if (!g) return '缺少组件尺寸配置：geometryConfig';
  if (typeof g.slotWidthMm !== 'number' || g.slotWidthMm <= 0) {
    return '缺少或非法字段：slotWidthMm（必须 > 0 的数字）';
  }
  if (typeof g.slotHeightMm !== 'number' || g.slotHeightMm <= 0) {
    return '缺少或非法字段：slotHeightMm（必须 > 0 的数字）';
  }
  if (
    g.slotRange != null &&
    (typeof g.slotRange !== 'number' || g.slotRange <= 0 || g.slotRange > 1)
  ) {
    return 'slotRange 必须在 (0, 1] 范围内';
  }
  return null;
}

/**
 * 取轮廓极值点（与 serviceHoleNew#getLowestPoint 一致：多并列时取靠近另一轴中线的点）。
 * @param {Array<Array<number>>} points - canvas 坐标 [[x,y],...]
 * @param {0|1} axis - 0=x、1=y
 * @param {boolean} isMax - 取最大还是最小
 * @returns {{x:number,y:number}}
 */
function getExtremePoint(points, axis, isMax) {
  let extreme = isMax ? -Infinity : Infinity;
  let collected = [];
  for (const p of points) {
    const v = p[axis];
    const better = isMax ? v > extreme : v < extreme;
    if (better) {
      extreme = v;
      collected = [p];
    } else if (v === extreme) {
      collected.push(p);
    }
  }
  if (!collected.length) return { x: 0, y: 0 };
  if (collected.length === 1) return { x: collected[0][0], y: collected[0][1] };
  const other = axis === 0 ? 1 : 0;
  const vals = collected.map((p) => p[other]);
  const mid = (Math.min.apply(null, vals) + Math.max.apply(null, vals)) / 2;
  let best = collected[0];
  let minD = Infinity;
  for (let i = 0; i < collected.length; i++) {
    const p = collected[i];
    const d = Math.abs(p[other] - mid);
    if (d < minD) {
      minD = d;
      best = p;
    }
  }
  return { x: best[0], y: best[1] };
}

/**
 * @param {number} n
 * @param {number} lo
 * @param {number} hi
 * @returns {number}
 */
function clamp(n, lo, hi) {
  if (lo > hi) return (lo + hi) / 2;
  return Math.max(lo, Math.min(hi, n));
}

/**
 * 计算内插口宽高（canvas px）。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {{ slotWidthPx: number, slotHeightPx: number }}
 */
function getSlotSizePx(instance, sharedState) {
  const g = instance.geometryConfig;
  const m2p = sharedState.mmToPixelRatio / (sharedState.maskFitScale || 1);
  return {
    slotWidthPx: g.slotWidthMm * m2p,
    slotHeightPx: g.slotHeightMm * m2p
  };
}

/**
 * placement 相对当前画布的缩放系数。
 * @param {Object} o - originSocketInfo 片段
 * @param {Object} sharedState
 * @returns {number}
 */
function getPlacementScale(o, sharedState) {
  // 按去除留白后的业务坐标宽度缩放，避免 padding 变化导致还原位置偏移。
  if (o && o.baseCanvasWidth && o.baseCanvasWidth !== sharedState.canvasWidth) {
    return getRestoreBusinessScale(o, sharedState);
  }
  return 1;
}

/**
 * 将 slotRect 的横向比例坐标还原为 canvas 坐标系下的插口左边界。
 * @param {Array} slotRect - 立牌插口 slotRect
 * @param {Object} sharedState - 共享画布状态
 * @returns {number|null} 插口左边界 canvas x；无法还原时返回 null
 */
function getSlotLeftCanvasXFromSlotRect(slotRect, sharedState) {
  // 缺少 slotRect 横向坐标时无法反推插口位置。
  if (!Array.isArray(slotRect) || !isFinite(Number(slotRect[0]))) {
    return null;
  }
  const slotXRatio = Number(slotRect[0]);
  const imageRatio = sharedState.imageInfo.width / sharedState.imageInfo.height;
  const skuRatio =
    sharedState.template.paperWidth / sharedState.template.paperHeight;
  const offsetX =
    imageRatio > skuRatio
      ? 0
      : (sharedState.imageInfo.height * skuRatio -
          sharedState.imageInfo.width) /
        2;
  const backendXBase = sharedState.imageRatioOver
    ? sharedState.imageInfo.width
    : sharedState.imageInfo.height / skuRatio;

  return (slotXRatio * backendXBase - offsetX) * sharedState.canvasScale;
}

/**
 * 从 originSocketInfo 显式偏移字段读取插口偏移比例。
 * @param {Object} o - originSocketInfo 片段
 * @returns {number|null} 插口偏移比例；缺少有效值时返回 null
 */
function getSocketOffsetRatioFromOriginField(o) {
  let ratio = null;
  // 新版 originSocketInfo 优先使用独立偏移字段恢复插口位置。
  if (o && typeof o.slotOffsetRatio === 'number') {
    ratio = o.slotOffsetRatio;
  } else {
    // 兼容旧数据中写在 slotRect[6] 的插口偏移比例。
    if (Array.isArray(o && o.slotRect) && typeof o.slotRect[6] === 'number') {
      ratio = o.slotRect[6];
    }
  }
  // 缺少有效偏移比例时继续走 slotRect 兼容还原。
  if (typeof ratio !== 'number' || !isFinite(ratio)) {
    return null;
  }
  return clamp(ratio, 0, 1);
}

/**
 * 从 originSocketInfo.slotRect[0] 和当前可移动范围反推插口偏移比例。
 * @param {Object} o - originSocketInfo 片段
 * @param {Object} instance - 立牌组件实例
 * @param {Object} sharedState - 共享画布状态
 * @returns {number|string|null} 横向偏移比例；超出范围时返回标记
 */
function getSocketOffsetRatioFromSlotRect(o, instance, sharedState) {
  const originRatio = getSocketOffsetRatioFromOriginField(o);
  // 显式偏移字段只服务本地二次编辑恢复，优先级高于接口 slotRect 口径。
  if (originRatio !== null) {
    return originRatio;
  }
  const c = instance.containerRect;
  const slotRect = o && o.slotRect;
  const slotLeftCanvasX = getSlotLeftCanvasXFromSlotRect(slotRect, sharedState);
  const sizes = getSlotSizePx(instance, sharedState);
  const moveRangeWidth = c ? c.w - sizes.slotWidthPx : 0;
  // 缺少可移动范围或插口位置时按默认居中处理。
  if (
    !c ||
    slotLeftCanvasX === null ||
    !isFinite(moveRangeWidth) ||
    moveRangeWidth <= 0
  ) {
    return null;
  }
  const rawRatio = (slotLeftCanvasX - c.x) / moveRangeWidth;
  // slotRect[0] 不在当前移动范围内时，先判断是否在允许的误差百分比内。
  if (rawRatio < 0 || rawRatio > 1) {
    // slotRect 导出时会保留小数，比例误差范围内按边界恢复，避免二次编辑误居中。
    if (
      rawRatio >= -LIPAI_SLOT_RECT_OUT_OF_RANGE_TOLERANCE_RATIO &&
      rawRatio <= 1 + LIPAI_SLOT_RECT_OUT_OF_RANGE_TOLERANCE_RATIO
    ) {
      return clamp(rawRatio, 0, 1);
    }
    return LIPAI_SLOT_RECT_OFFSET_OUT_OF_RANGE;
  }
  return clamp(rawRatio, 0, 1);
}

/**
 * 按横向偏移比例计算当前可移动范围内的插口中心点。
 * @param {Object} instance - 立牌组件实例
 * @param {Object} sharedState - 共享画布状态
 * @param {number} ratio - 横向偏移比例
 * @returns {number|null} 插口中心点 x 坐标
 */
function restoreLiPaiSlotCenterXByOffsetRatio(instance, sharedState, ratio) {
  const c = instance.containerRect;
  // 缺少当前移动范围时无法按比例恢复。
  if (!c) {
    return null;
  }
  const sizes = getSlotSizePx(instance, sharedState);
  const moveRangeWidth = c.w - sizes.slotWidthPx;
  // 没有可移动范围时保持居中。
  if (!isFinite(moveRangeWidth) || moveRangeWidth <= 0) {
    return c.x + c.w / 2;
  }
  return c.x + sizes.slotWidthPx / 2 + moveRangeWidth * ratio;
}

/**
 * 按添加时刻 outline 初始化外容器（bottom 贴底）；写入 instance.containerRect。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {void}
 */
function initContainer(instance, sharedState) {
  const g = instance.geometryConfig;
  const defaultSlotRange = DEFAULT_CONFIG.liPaiSlotRangePercent / 100;
  const slotRange = g.slotRange != null ? g.slotRange : defaultSlotRange;
  const sizes = getSlotSizePx(instance, sharedState);
  const slotWidthPx = sizes.slotWidthPx;
  const slotHeightPx = sizes.slotHeightPx;
  const points = sharedState.outlinePoints;

  // 立牌插口可移动范围 = SKU 纸张宽度对应像素 × 比例，保持首次渲染旧口径。
  let containerW =
    sharedState.template.paperWidth * sharedState.mmToPixelRatio * slotRange;
  if (containerW > sharedState.drawImageWidth) {
    containerW = sharedState.drawImageWidth;
  }
  // 最小不低于立牌自身宽度，防止极端窄轮廓导致插口无法移动
  if (containerW < slotWidthPx) containerW = slotWidthPx;

  const anchorPt = getExtremePoint(points, 1, true);
  const outlineMinX = Math.min.apply(
    null,
    points.map(function (p) {
      return p[0];
    })
  );
  const outlineMaxX = Math.max.apply(
    null,
    points.map(function (p) {
      return p[0];
    })
  );
  // 容器以轮廓水平中点居中，避免最低点偏侧时移动范围偏移。
  let containerCenterX = (outlineMinX + outlineMaxX) / 2;
  let containerLeft = containerCenterX - containerW / 2;
  let containerRight = containerCenterX + containerW / 2;

  if (containerLeft < sharedState.imageLeft) {
    containerLeft = sharedState.imageLeft;
    containerCenterX = sharedState.imageLeft + containerW / 2;
    containerRight = containerCenterX + containerW / 2;
  }
  if (containerRight > sharedState.imageRight) {
    containerRight = sharedState.imageRight;
    containerCenterX = sharedState.imageRight - containerW / 2;
    containerLeft = containerCenterX - containerW / 2;
  }

  instance.containerRect = {
    x: containerLeft,
    y: anchorPt.y,
    w: containerW,
    h: slotHeightPx
  };
}

/**
 * 在已冻结的 containerRect 内更新内插口（需 sharedState 算尺寸）。
 * @param {Object} instance
 * @param {Object} sharedState
 * @param {number|null|undefined} slotCenterX
 * @returns {void}
 */
function updateSlotInContainerWithState(instance, sharedState, slotCenterX) {
  const c = instance.containerRect;
  if (!c) return;
  const sizes = getSlotSizePx(instance, sharedState);
  const slotWidthPx = sizes.slotWidthPx;
  const slotHeightPx = sizes.slotHeightPx;

  const cxLo = c.x + slotWidthPx / 2;
  const cxHi = c.x + c.w - slotWidthPx / 2;
  let slotCx =
    slotCenterX != null && !isNaN(slotCenterX) ? slotCenterX : c.x + c.w / 2;
  slotCx = clamp(slotCx, cxLo, cxHi);

  instance.slotRect = {
    x: slotCx - slotWidthPx / 2,
    y: c.y,
    w: slotWidthPx,
    h: slotHeightPx
  };
  instance.slotCenter = {
    x: slotCx,
    y: c.y + slotHeightPx / 2
  };
  instance.dragAxis = 'x';
}

/**
 * init：还原或首次初始化外容器 + 内插口。
 * @param {Object} instance
 * @param {Object} sharedState
 * @param {Object} options
 * @param {Object} [options.placement] - { originSocketInfo }
 * @returns {void}
 */
function init(instance, sharedState, options) {
  const placement = options && options.placement;
  const o = placement && placement.originSocketInfo;
  let slotCenterX = null;

  // 当前运行态编辑返回的完整 componentData 继续使用原容器；后端回填数据重新生成容器。
  if (
    o &&
    o.containerPoint &&
    typeof o.containerWidth === 'number' &&
    typeof o.containerHeight === 'number'
  ) {
    const k = getPlacementScale(o, sharedState);
    const sizes = getSlotSizePx(instance, sharedState);
    const anchorPt = getExtremePoint(sharedState.outlinePoints, 1, true);
    instance.containerRect = {
      x: o.containerPoint.x * k,
      y: anchorPt.y,
      w: o.containerWidth * k,
      h: sizes.slotHeightPx
    };
  } else {
    initContainer(instance, sharedState);
  }

  const socketOffsetRatio = getSocketOffsetRatioFromSlotRect(
    o,
    instance,
    sharedState
  );

  // slotRect[0] 落在当前移动范围外时，按默认居中位置恢复。
  if (socketOffsetRatio === LIPAI_SLOT_RECT_OFFSET_OUT_OF_RANGE) {
    slotCenterX = null;
  } else if (socketOffsetRatio !== null) {
    slotCenterX = restoreLiPaiSlotCenterXByOffsetRatio(
      instance,
      sharedState,
      socketOffsetRatio
    );
  } else if (
    o &&
    o.point &&
    typeof o.width === 'number' &&
    typeof o.height === 'number'
  ) {
    const k = getPlacementScale(o, sharedState);
    slotCenterX = (o.point.x + o.width / 2) * k;
  }

  updateSlotInContainerWithState(instance, sharedState, slotCenterX);
}

/**
 * clampSelf：仅重夹内插口，不重算外容器。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {boolean} 恒 false（立牌不通过 clampSelf 报 outline 违规）
 */
function clampSelf(instance, sharedState) {
  if (instance.slotCenter) {
    updateSlotInContainerWithState(
      instance,
      sharedState,
      instance.slotCenter.x
    );
  }
  return false;
}

/**
 * 命中检测：外容器内任意位置可命中。
 * @param {Object} instance
 * @param {{x:number,y:number}} point
 * @returns {boolean}
 */
function hitTest(instance, point) {
  const c = instance.containerRect;
  if (!c) return false;
  return (
    point.x >= c.x &&
    point.x <= c.x + c.w &&
    point.y >= c.y &&
    point.y <= c.y + c.h
  );
}

/**
 * 拖动起点：记录插口中心与指针，避免从容器空白起拖瞬移。
 * @param {Object} instance
 * @param {{x:number,y:number}} point
 * @returns {void}
 */
function onDragStart(instance, point) {
  instance._dragRef = {
    px: point.x,
    cx: instance.slotCenter.x
  };
}

/**
 * 拖动：仅更新内插口 centerX。
 * @param {Object} instance
 * @param {{x:number,y:number}} point
 * @param {Object} sharedState
 * @returns {void}
 */
function onDragMove(instance, point, sharedState) {
  const ref = instance._dragRef;
  if (!ref) return;
  const nextX = ref.cx + (point.x - ref.px);
  updateSlotInContainerWithState(instance, sharedState, nextX);
}

/**
 * @param {Object} instance
 * @returns {void}
 */
function onDragEnd(instance) {
  instance._dragRef = null;
}

/**
 * 编辑模式：外容器虚线 + 内插口实色。
 * @param {Object} instance
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} sharedState
 * @returns {void}
 */
function drawEdit(instance, ctx, sharedState) {
  const c = instance.containerRect;
  const s = instance.slotRect;
  if (!c || !s) return;
  ctx.save();

  ctx.setLineDash([2, 2]);
  ctx.strokeStyle = lipaiColor;
  ctx.fillStyle = 'rgba(64, 123, 255, 0.10)';
  ctx.lineWidth = Math.max(sharedState.lineWidth, 1);
  ctx.beginPath();
  ctx.rect(c.x, c.y, c.w, c.h);
  ctx.fill();
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.fillStyle = lipaiColor + '33';
  ctx.beginPath();
  ctx.rect(s.x, s.y, s.w, s.h);
  ctx.fill();
  ctx.stroke();

  ctx.restore();
}

/**
 * 预览模式（效果图）：不绘制 container / slot；槽口形状由共享 outlinePoints 刀线体现（spec §4.4）。
 * @param {Object} instance
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} sharedState
 * @returns {void}
 */
function drawPreview(/* instance, ctx, sharedState */) {
  return;
}

/**
 * @param {Object} instance
 * @returns {Object}
 */
function getCollisionShape(instance) {
  const s = instance.slotRect;
  return { kind: 'rect', x: s.x, y: s.y, w: s.w, h: s.h };
}

/**
 * @param {Object} instance
 * @returns {{x:number,y:number,w:number,h:number}}
 */
function getSelectionRect(instance) {
  const c = instance.containerRect;
  return { x: c.x, y: c.y, w: c.w, h: c.h };
}

/**
 * @param {Object} instance
 * @returns {{x:number,y:number}}
 */
function getMagnifierAnchor(instance) {
  return { x: instance.slotCenter.x, y: instance.slotCenter.y };
}

/**
 * 计算立牌插口在可移动范围内的偏移比例。
 * @param {Object} instance - 立牌组件实例
 * @returns {number|null} 插口偏移比例；无法计算时返回 null
 */
function getLiPaiSocketOffsetRatio(instance) {
  const c = instance.containerRect;
  const s = instance.slotRect;
  // 缺少移动范围或插口矩形时无法计算偏移比例。
  if (!c || !s) {
    return null;
  }
  const moveRangeWidth = c.w - s.w;
  // 移动范围非法时不写入偏移比例。
  if (!isFinite(moveRangeWidth) || moveRangeWidth <= 0) {
    return null;
  }
  const ratio = (s.x - c.x) / moveRangeWidth;
  // 计算结果非法时不写入偏移比例。
  if (!isFinite(ratio)) {
    return null;
  }
  return clamp(ratio, 0, 1);
}

/**
 * 导出 layerData 片段。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {Object}
 */
function toExport(instance, sharedState) {
  const s = instance.slotRect;
  const c = instance.containerRect;
  const beTopLeft = convertCanvasToBackend({ x: s.x, y: s.y }, sharedState);
  const beWidth = Math.round(s.w / sharedState.canvasScale);
  const beHeight = Math.round(s.h / sharedState.canvasScale);
  const slotRect = rectCanvasToApiSlotRect(s, sharedState);
  const restoreSlotRect = rectCanvasToSlotRect(s, sharedState);
  const socketOffsetRatio = getLiPaiSocketOffsetRatio(instance);
  const socketInfoItem = {
    point: beTopLeft,
    width: beWidth,
    height: beHeight,
    slotRect,
    componentId: instance.id
  };
  const originItem = {
    point: { x: s.x, y: s.y },
    width: s.w,
    height: s.h,
    slotRect: restoreSlotRect.slice(),
    slotOffsetRatio: socketOffsetRatio,
    containerPoint: { x: c.x, y: c.y },
    containerWidth: c.w,
    containerHeight: c.h,
    baseCanvasWidth: sharedState.canvasWidth,
    canvasRoundWhiteSpace: sharedState.canvasRoundWhiteSpace,
    componentId: instance.id,
    geometryConfig: instance.geometryConfig,
    sizePresetId: instance.sizePresetId || '',
    enableMagnifier: instance.enableMagnifier !== false
  };
  return {
    socketInfoList: [socketInfoItem],
    originSocketInfoList: [originItem]
  };
}

/**
 * dirty 比较：外容器 + 内插口位置。
 * @param {Object} instance
 * @returns {Object}
 */
function serializeForDirty(instance) {
  const s = instance.slotRect;
  const c = instance.containerRect;
  return {
    cx: Math.round(c.x * 100) / 100,
    cy: Math.round(c.y * 100) / 100,
    cw: Math.round(c.w * 100) / 100,
    ch: Math.round(c.h * 100) / 100,
    sx: Math.round(s.x * 100) / 100,
    sy: Math.round(s.y * 100) / 100
  };
}

/**
 * 供 orchestrator 调 imageBorderParse 的 slotRect。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {Array<number>}
 */
function getSlotRectForApi(instance, sharedState) {
  const slotRect = rectCanvasToApiSlotRect(instance.slotRect, sharedState);
  const g = instance.geometryConfig || {};
  // imageBorderParse 需要当前插口物理规格，避免 api.js 回退到模板默认 slotWidth/slotHeight。
  if (typeof g.slotWidthMm === 'number' && typeof g.slotHeightMm === 'number') {
    slotRect[4] = g.slotWidthMm;
    slotRect[5] = g.slotHeightMm;
  }
  return slotRect;
}

const liPaiStrategy = {
  type: DRAW_TYPE.LI_PAI,
  validateGeometryConfig,
  init,
  clampSelf,
  hitTest,
  onDragStart,
  onDragMove,
  onDragEnd,
  drawEdit,
  drawPreview,
  getCollisionShape,
  getSelectionRect,
  getMagnifierAnchor,
  toExport,
  serializeForDirty,
  getSlotRectForApi
};

registerComponentType(liPaiStrategy);

export default liPaiStrategy;
